import React from 'react';
import { useDispatch } from 'react-redux';
import { FormattedDate } from 'react-intl';
import { Buffer } from 'buffer';
import { Box, Card, CardContent, CardHeader, Chip, Typography } from '@mui/material';

import { type FilesViewerProps } from '../FilesViewer';
import { executeApi } from '../../Utils';
import { getFileDataType, type MiddlewhereFile } from '../../files-explorer/FilesUtils';
import useSettings from '../../hooks/useSettings';
import Icon from '../../Icon';
import SquareChip from '../../SquareChip';

interface MailMessage {
	senders: string[];
	recipients: string[];
	subject: string;
	direction: 'inbound' | 'outbound';
	body: string;
	bodyHtml: string;
	attachments: MiddlewhereFile[];
	sentDate: string;
	receivedDate: string;
	importance: string;
}

export default function MsgViewer({ filePath }: FilesViewerProps) {
	const dispatch = useDispatch();
	const { t } = useSettings();

	const [mailMessage, setMailMessage] = React.useState<MailMessage>();

	const getAttachmentContent = attachment => {
		if (attachment.content)
			return `data:${getFileDataType(attachment)};base64,${Buffer.from(attachment.content, 'base64')}`;
		return undefined;
	};

	React.useEffect(() => {
		executeApi.get({
			url: `/api/filexchange/file/mail?path=${filePath}`,
			dispatch,
			successHandler: ({ data }) => setMailMessage(data)
		});
	}, [filePath, dispatch]);

	return mailMessage ? (
		<Card className="rounded-6" variant="outlined">
			<CardHeader
				title={
					<>
						<Typography>
							<b>{`${t('MAIL_FROM')}: `}</b>
							{mailMessage.senders.join(', ')}
						</Typography>
						<Typography>
							<b>{`${t('MAIL_TO')}: `}</b>
							{mailMessage.recipients.join(', ')}
						</Typography>
						<Typography>
							<b>{`${t('MAIL_DATE_SENT')}: `}</b>
							<FormattedDate
								value={mailMessage.sentDate}
								year="numeric"
								month="long"
								day="numeric"
								hour="numeric"
								minute="numeric"
							/>
						</Typography>
						{mailMessage.receivedDate && (
							<Typography>
								<b>{`${t('MAIL_DATE_RECEIVED')}: `}</b>
								<FormattedDate
									value={mailMessage.receivedDate}
									year="numeric"
									month="long"
									day="numeric"
									hour="numeric"
									minute="numeric"
								/>
							</Typography>
						)}
						<Typography gutterBottom>
							<b>{`${t('MAIL_SUBJECT')}: `}</b>
							{mailMessage.subject}
						</Typography>
					</>
				}
				subheader={
					<>
						{mailMessage.direction && (
							<SquareChip label={t(`MAIL_${mailMessage.direction.toUpperCase()}`)} />
						)}
						{mailMessage.importance && <SquareChip label={mailMessage.importance} />}
					</>
				}
			/>
			<CardContent>
				{mailMessage.bodyHtml ? (
					<div dangerouslySetInnerHTML={{ __html: mailMessage.bodyHtml }} />
				) : (
					<Typography className="whitespace-pre-line">{mailMessage.body}</Typography>
				)}
				{mailMessage.attachments?.length > 0 && (
					<Box
						sx={theme => ({
							marginTop: theme.spacing(2),
							paddingTop: theme.spacing(1),
							borderTop: `1px solid ${theme.palette.divider}`
						})}
					>
						{mailMessage.attachments.map((attachment, index) => (
							<Chip
								key={index}
								className="mb-4 mr-4"
								icon={<Icon icon="download" />}
								label={<Typography fontStyle="normal">{attachment.name}</Typography>}
								clickable
								component="a"
								href={getAttachmentContent(attachment)}
								download={attachment.name}
							/>
						))}
					</Box>
				)}
			</CardContent>
		</Card>
	) : (
		<></>
	);
}
